import React, { useState } from "react";
import { Mail, MapPin, Phone, Clock, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import { COMPANY } from "../lib/siteData";
import SEO from "../components/SEO";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone are required");
    setLoading(true);
    try {
      await submitLead({ ...form, source: "contact" });
      toast.success("Thank you! Our team will contact you within 24 hours.");
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with CNGTPL Solar. Free consultation, site visit and quote." />
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container-narrow">
          <div className="badge-eco bg-blue-600/20 border-blue-600/40 text-blue-300 mb-3">Contact Us</div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight mb-3">Talk to our solar experts</h1>
          <p className="text-slate-300 max-w-2xl">Call, WhatsApp or drop a message. Our team responds within 24 hours (usually faster).</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-narrow grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-5">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-slate-200 rounded-2xl p-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                <Input placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="contact-city" />
              </div>
              <Textarea placeholder="Tell us about your requirement (roof size, bill, etc.)" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
              <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 py-6 text-base" data-testid="contact-submit">
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>

          <aside className="lg:col-span-2 space-y-4">
            {[
              { icon: MapPin, title: "Head Office", content: COMPANY.address },
              { icon: Phone, title: "Phone", content: COMPANY.phone, href: `tel:${COMPANY.phone}` },
              { icon: Mail, title: "Email", content: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: Clock, title: "Business Hours", content: COMPANY.hours },
            ].map((b) => (
              <div key={b.title} className="rounded-2xl border border-slate-200 p-5 bg-white">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-slate-900 mb-0.5">{b.title}</div>
                    {b.href ? (
                      <a href={b.href} className="text-sm text-slate-600 hover:text-blue-700">{b.content}</a>
                    ) : (
                      <div className="text-sm text-slate-600">{b.content}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <a
              href={`https://wa.me/${COMPANY.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-2xl p-5 transition"
              data-testid="contact-whatsapp-btn"
            >
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><MessageCircle className="w-5 h-5" fill="white" strokeWidth={0} /></div>
              <div>
                <div className="font-heading font-semibold">Chat on WhatsApp</div>
                <div className="text-xs text-blue-50">Instant response 9 AM — 9 PM</div>
              </div>
            </a>
          </aside>
        </div>
      </section>

      {/* Map */}
      <section className="pb-16">
        <div className="container-narrow">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              title="CNGTPL Solar Office Map"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(COMPANY.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
              width="100%"
              height="380"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}
